import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import getLeaseSlug from '../../../../defaultData/getLeaseSlug';
import { SpaceSection } from '../../SpaceSection';
import { NearestLocation } from '../../../../graphql/graphql';
import {
  CurrentItemAddress,
  LocationLink,
  StyledMap,
  LocationList,
  MapWrapper,
  MapInfo,
  MapOverlay,
  ShowMapButton,
  ShowMapButtonText,
  ShowOnMap
} from './styled';
import useScreenSize from '../../../../hooks/useScreenSize';
import Icon from '../../../Icon';
import PinIcon from '../../../../public/static/images/item/pin-icon.svg';
import {
  HiddenOnDesktopBlock,
  HiddenOnMobileBlock
} from '../CurrentItemSections/styled';
import { useClient } from '../../../../hooks/useClient';
import { useGoogleMaps } from '../../../../providers/GoogleMapsProvider';
import { useResponsive } from '../../../../hooks/useResponsive';

interface CurrentItemLocationSectionProps {
  city?: string | null;
  address?: string | null;
  lat?: number | null;
  lng?: number | null;
  nearestLocations?: NearestLocation[] | null;
  categorySlug: string;
  isLeased: boolean;
}

export const CurrentItemLocationSection: React.FC<
  CurrentItemLocationSectionProps
> = ({ city, address, lat, lng, nearestLocations, categorySlug, isLeased }) => {
  const { isSmall } = useScreenSize();
  const { showOnTabledOrMobile, hideOnMobile } = useResponsive();
  const isClient = useClient();
  const { t } = useTranslation();
  const {
    setIsInitializedMap,
    isOpenModal,
    toggleModal,
    setMapData,
    setMapModalData
  } = useGoogleMaps();

  const leaseSlug = getLeaseSlug(isLeased);
  const coordinates = lat && lng ? { lat, lng } : undefined;

  const locationAddress = (
    <CurrentItemAddress data-cy="cur-location__address" tag="title_4_new">
      {`${city}, ${address}`}
    </CurrentItemAddress>
  );

  const pinIcon = (
    <Icon
      width={isSmall ? 13 : 15}
      height={isSmall ? 16 : 18}
      IconComponent={PinIcon}
      indent={false}
    />
  );

  const shouldRenderMapOverlay = isSmall && !isOpenModal && isClient;
  const showOnMap = t('pages.current_item.buttons.view_map');

  const handlePreviewMapClick = () => {
    if (isSmall) toggleModal();
  };

  const setOpenMapData = useCallback(() => {
    setMapData({
      coordinates,
      initialLocation: coordinates,
      pinSelected: !!(!isSmall && isOpenModal),
      isMarkerDraggable: false
    });
    setIsInitializedMap(true);
    setMapModalData({
      modalId: 'current-item-map'
    });
  }, [setIsInitializedMap, setMapModalData, coordinates, isSmall, isOpenModal]);

  const handleOpenMap = useCallback(() => {
    setOpenMapData();
    toggleModal();
  }, [toggleModal, setOpenMapData]);

  return (
    <SpaceSection>
      <div data-cy="cur-location">
        {showOnTabledOrMobile && (
          <HiddenOnDesktopBlock>{locationAddress}</HiddenOnDesktopBlock>
        )}

        <MapWrapper>
          <MapInfo>
            <StyledMap
              data-cy="cur-location__map"
              role="button"
              aria-label="Open location"
              onClick={handlePreviewMapClick}
              tabIndex={0}
            >
              {shouldRenderMapOverlay && (
                <MapOverlay data-stat="item-location__map">
                  <ShowMapButton type="button" onClick={setOpenMapData}>
                    {pinIcon}
                    <ShowMapButtonText tag="body_1">
                      {showOnMap}
                    </ShowMapButtonText>
                  </ShowMapButton>
                </MapOverlay>
              )}

              {!isSmall && isClient && pinIcon}
            </StyledMap>

            {hideOnMobile && (
              <HiddenOnMobileBlock>{locationAddress}</HiddenOnMobileBlock>
            )}
          </MapInfo>

          <ShowOnMap
            tag="body_1"
            onClick={handleOpenMap}
            aria-label="Open map"
            data-stat="item-location__map"
          >
            {showOnMap}
          </ShowOnMap>
        </MapWrapper>

        <LocationList data-cy="cur-location__locations">
          {nearestLocations?.map(item => (
            <LocationLink
              href={`${item.path}/${leaseSlug}/${categorySlug}`}
              key={item.id}
              data-cy="cur-location__link"
            >
              {item?.fullName || item.name}
            </LocationLink>
          ))}
        </LocationList>
      </div>
    </SpaceSection>
  );
};
